import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";

export function PromoCodesSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ code: "", discount_percent: "0", discount_fixed: "0", is_active: true, max_uses: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  const load = async () => {
    const { data } = await supabase.from("promo_codes").select("*");
    setRows(data || []);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const payload: any = {
      code: form.code,
      discount_percent: Number(form.discount_percent),
      discount_fixed: Number(form.discount_fixed),
      is_active: form.is_active,
    };
    if (form.max_uses) payload.max_uses = Number(form.max_uses);
    if (editingId) {
      const { error } = await supabase.from("promo_codes").update(payload).eq("id", editingId);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    } else {
      const { error } = await supabase.from("promo_codes").insert(payload);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    }
    resetForm();
    load();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await supabase.from("promo_codes").delete().eq("id", deleteId);
    setDeleteId(null);
    load();
  };

  const resetForm = () => {
    setForm({ code: "", discount_percent: "0", discount_fixed: "0", is_active: true, max_uses: "" });
    setEditingId(null);
    setIsDialogOpen(false);
  };

  const openCreate = () => { resetForm(); setIsDialogOpen(true); };
  const openEdit = (row: any) => {
    setForm({ code: row.code, discount_percent: String(row.discount_percent), discount_fixed: String(row.discount_fixed), is_active: row.is_active, max_uses: row.max_uses ? String(row.max_uses) : "" });
    setEditingId(row.id);
    setIsDialogOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Códigos Promocionales</h2>
          <p className="text-muted-foreground text-sm mt-1">Gestiona descuentos y promociones <Badge variant="secondary" className="ml-2">{rows.length}</Badge></p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Tag className="h-10 w-10 mb-3 opacity-40" /><p>No hay códigos promocionales</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>% Desc</TableHead>
                  <TableHead>$ Desc</TableHead>
                  <TableHead>Usos</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[100px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell><Badge variant="outline" className="font-mono">{row.code}</Badge></TableCell>
                    <TableCell>{row.discount_percent}%</TableCell>
                    <TableCell>${row.discount_fixed}</TableCell>
                    <TableCell>{row.current_uses}/{row.max_uses ?? "∞"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={row.is_active ? "bg-green-500/15 text-green-700 border-green-200" : "bg-red-500/15 text-red-700 border-red-200"}>
                        {row.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(row.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Editar Código" : "Nuevo Código"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Código</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="ej. SUMMER20" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Descuento %</Label><Input type="number" value={form.discount_percent} onChange={e => setForm({ ...form, discount_percent: e.target.value })} /></div>
              <div><Label>Descuento Fijo $</Label><Input type="number" value={form.discount_fixed} onChange={e => setForm({ ...form, discount_fixed: e.target.value })} /></div>
            </div>
            <div><Label>Máx. Usos (vacío = ilimitado)</Label><Input type="number" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
              <Label>Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>{editingId ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar código?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
